/**
 * D6 · নিশ্চিতকরণ — `/checkout/done`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D6 (360 × 680).
 *
 * Frame note: "Amount and order number big enough to read aloud over a phone call —
 * which is how a delivery problem actually gets resolved here. Two exits, no dead end."
 *
 * The tick is drawn, not an icon: a 68px `3px solid #1E6B4A` ring containing a
 * `26 × 14` box with only its left and bottom borders, rotated −45°.
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * D5 hands this screen the reference of the order it just placed, in router state, and
 * the order is read back with `GET /v1/orders/:id`. Opened cold — a bookmark, a reload,
 * the visual gate — there is no state to read, so it falls back to `GET /v1/orders?limit=1`:
 * "the order you just placed" and "your most recent order" are the same row in the case
 * this screen exists for, and the fallback is the honest reading of the URL on its own.
 *
 * CONFLICT, REPORTED NOT RESOLVED: a partner-disclosure panel was previously added here
 * on the strength of docs/05 § fulfilment ("the client must display it on every money
 * screen"). The frame draws no such panel, and it is not a small addition: the confirmed
 * column is vertically centred, so a 60px block at its foot lifts the tick, the title,
 * the figure and the detail table ~52px off their drawn positions — the single largest
 * contributor to this screen's 4.72% diff. docs/01 is the binding document for this
 * work ("Read the exact value in the .dc.html. Use that value."), so the frame wins and
 * the panel is gone. The disclosure is still shown at the moment of payment (D5) and on
 * the receipt (D7). Whether D6 must carry it too is a copy-and-compliance decision for a
 * human; if the answer is yes, the FRAME needs redrawing, not the route.
 */
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import type { OrderResponse } from '@oprappo/contracts';
import { assertPartnerDisclosure } from '@oprappo/contracts';
import { Frame } from '../offering/chrome.js';
import { Loaded, commerce, useResource } from '../offering/commerce.js';

export function D6DoneView({ order }: { order: OrderResponse }): JSX.Element {
  const navigate = useNavigate();
  // The licensing rule, checked before a figure is painted: an amount on a money screen
  // with no partner named is the one thing no Oprappo screen may show (docs/05).
  assertPartnerDisclosure(order);
  const c = order.confirmation;

  return (
    <Frame className="bg-sand border border-border px-[16px] pt-[20px] pb-[16px]">
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="w-[68px] h-[68px] box-content border-[3px] border-success rounded-full flex items-center justify-center mb-[24px]">
          <div className="w-[26px] h-[14px] box-content border-l-[3px] border-b-[3px] border-success -mt-[6px] rotate-[-45deg]" />
        </div>
        <div className="font-anek font-semibold text-[24px] leading-[1.25] text-ink">
          {c.title_bn}
        </div>
        <div className="font-manrope font-bold text-[30px] leading-[1.1] text-ink tabular-nums mt-[14px]">
          {order.total.display}
        </div>
        <div className="font-anek font-normal text-[15px] leading-[1.75] text-body mt-[12px]">
          {c.eta_lines_bn[0]}
          <br />
          {c.eta_lines_bn[1]}
        </div>

        {/*
         * `box-content` is the frame's arithmetic. The design draws
         * `width:100%;border:1px solid #E2DED5` with no `box-sizing` override, so this
         * card is 326 of content plus 2 of border = 328 wide, and its centring parent
         * hangs that extra pixel off each side: measured left 16 in a frame whose
         * content box starts at 17. Under this app's border-box reset it came out 326
         * wide at left 17 — 2px narrow, 1px right, and every row inside it with it.
         */}
        <div className="bg-white border border-border w-full box-content mt-[26px]">
          {c.rows.map((r, i) => (
            <div
              key={r.label_bn}
              className={
                'px-[14px] py-[12px] flex justify-between items-baseline' +
                (i < c.rows.length - 1 ? ' border-b border-border' : '')
              }
            >
              <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
                {r.label_bn}
              </div>
              {/*
               * The server says which script a value is in. Manrope carries no Bangla
               * digits and falls back silently at the wrong size (docs/02 § numerals),
               * so this is not styling — it is which font can render the glyphs at all.
               */}
              <div
                className={
                  r.script === 'latin' || r.script === 'money'
                    ? 'font-manrope font-semibold text-[14.5px] leading-[1.3] text-ink'
                    : 'font-anek font-medium text-[14.5px] leading-[1.75] text-ink'
                }
              >
                {r.value}
              </div>
            </div>
          ))}
        </div>

      </div>

      {/*
       * Both exits are drawn `height:52px` with `600 15px/1.75`. § 02's Button pairs
       * 52px with 16px and 15px with 48px, so this combination does not exist in the
       * component. The height comes from `lg`; the 15px is forced with `!text-[15px]`,
       * because Tailwind emits arbitrary font sizes in ascending order and a plain
       * `text-[15px]` would lose the cascade to the component's own `text-[16px]`.
       * The right fix is a `size` in the component that pairs 52px with 15px — a
       * packages/ui change, which WP-D does not own. Reported.
       *
       * `box-content` on the secondary is the frame's arithmetic, not a tweak: the
       * design draws `border:1.5px solid #0E3A45;height:52px` with no `box-sizing`
       * override, so that box is 55px tall next to a 52px primary.
       */}
      <div className="flex gap-[10px]">
        <Button
          tone="secondary"
          onClick={() => navigate('/')}
          className="flex-1 box-content p-0 !text-[15px]"
        >
          {c.home_label_bn}
        </Button>
        {/*
         * `p-0` on BOTH, and on the primary it is load-bearing. styles.css's reset
         * gives `button` only `font`, `color`, `margin` and `background` — never
         * `padding` — so a bare <button> keeps the UA's `1px 6px`. On a `w-full`
         * button that is invisible; on a `flex-1` one it is not: those 12px become the
         * item's hypothetical main size, so the pair resolved 153 / 163 instead of the
         * drawn 159 / 157. Reported as a shared fix (see the report): `padding: 0` in
         * the Button component, or a `button { padding: 0 }` line in the reset.
         */}
        <Button
          onClick={() => {
            // Null when no activity row was written for this order. The button is drawn
            // unconditionally, so it stays drawn and does nothing rather than navigating
            // to `/activity/null`.
            if (order.activity_ref !== null) navigate(`/activity/${order.activity_ref}`);
          }}
          className="flex-1 p-0 !text-[15px]"
        >
          {c.view_order_label_bn}
        </Button>
      </div>
    </Frame>
  );
}

/** What D5 puts in router state when it navigates here. */
interface DoneState {
  reference?: string;
}

export function D6Done(): JSX.Element {
  const location = useLocation();
  const state = location.state as DoneState | null;
  const reference = state?.reference ?? null;

  const load = useCallback(
    async (signal: AbortSignal): Promise<OrderResponse> => {
      if (reference !== null) return commerce.order(reference, signal);
      const { orders } = await commerce.latestOrder(signal);
      const latest = orders[0];
      if (latest === undefined) {
        // No order to confirm. There is no drawn state for arriving at a confirmation
        // screen with nothing to confirm, so nothing is rendered rather than a screen
        // nobody designed — see `docs/bangla-needed/commerce.md`.
        throw new Error('no order to confirm');
      }
      return latest;
    },
    [reference],
  );
  const [resource] = useResource(load, `order:${reference ?? 'latest'}`);

  return <Loaded resource={resource}>{(order) => <D6DoneView order={order} />}</Loaded>;
}
